import React, { useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  Rocket,
  Search,
  Type,
  FileText,
  Image as ImageIcon,
  Clapperboard,
  Zap,
  CheckCircle2,
  Loader2,
  Download,
  Copy,
  AlertCircle,
  Sparkles,
  LayoutDashboard,
  Youtube,
  Hash,
  ListOrdered,
  PenTool,
  X,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init";
import { validateGeminiKey } from "../../../utils/geminiUtils";

const AllInYouTube = () => {
  const [user, loadingAuth] = useAuthState(auth);
  const [keyword, setKeyword] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Results State
  const [results, setResults] = useState({
    research: null,
    title: "",
    metadata: null,
    thumbnail: null,
    scenes: [],
    finalResult: null
  });

  // Step Status State: 'idle', 'loading', 'success', 'error'
  const [stepStatus, setStepStatus] = useState([
    'idle', 'idle', 'idle', 'idle', 'idle', 'idle'
  ]);

  const steps = [
    { name: "Keyword Research", icon: <Search size={20} />, description: "Analyzing trends and viral ideas" },
    { name: "SEO Title", icon: <Type size={20} />, description: "Crafting high-CTR titles" },
    { name: "Metadata", icon: <FileText size={20} />, description: "Generating description and tags" },
    { name: "Thumbnail", icon: <ImageIcon size={20} />, description: "Rendering clickbait thumbnail" },
    { name: "Scene Generation", icon: <Clapperboard size={20} />, description: "Creating 7-8 video scenes" },
    { name: "Script & SEO", icon: <Zap size={20} />, description: "Finalizing hook and recommendations" }
  ];

  const updateStepStatus = (index, status) => {
    setStepStatus(prev => {
      const newStatus = [...prev];
      newStatus[index] = status;
      return newStatus;
    });
  };

  // --- Helper: Render formatted text with proper markdown parsing ---
  const renderFormattedText = (text) => {
    if (!text) return null;

    // First, separate Tags section from main content
    const tagsMatch = text.match(/(?:^|\n)\s*Tags:\s*([\s\S]*?)$/i);
    let mainContent = text;
    let tagsContent = null;

    if (tagsMatch) {
      mainContent = text.slice(0, text.lastIndexOf('Tags:')).trim();
      tagsContent = tagsMatch[1];
    }

    // Process main content - split into blocks
    const processContent = (content) => {
      // Split by double newlines for paragraphs
      const blocks = content.split(/\n\n+/);

      return blocks.map((block, blockIdx) => {
        block = block.trim();
        if (!block) return null;

        // Handle headings (lines starting with ** and ending with **)
        const headingMatch = block.match(/^\*\*(.+?)\*\*\s*[-:]?\s*$/);
        if (headingMatch) {
          return (
            <h4 key={blockIdx} className="fw-bold text-white mb-2 mt-4">
              {headingMatch[1]}
            </h4>
          );
        }

        // Handle numbered lists
        if (/^\d+\.\s/.test(block)) {
          const items = block.split(/\n/).filter(line => /^\d+\.\s/.test(line.trim()));
          return (
            <ul key={blockIdx} className="list-unstyled mb-3">
              {items.map((item, i) => {
                const cleanItem = item.replace(/^\d+\.\s*/, '').trim();
                return (
                  <li key={i} className="d-flex align-items-start gap-2 mb-2 text-white-80">
                    <span className="text-primary flex-shrink-0 mt-1">•</span>
                    <span dangerouslySetInnerHTML={{ __html: formatInlineText(cleanItem) }} />
                  </li>
                );
              })}
            </ul>
          );
        }

        // Handle bullet lists
        if (/^[-•]\s/.test(block)) {
          const items = block.split(/\n/).filter(line => /^[-•]\s/.test(line.trim()));
          return (
            <ul key={blockIdx} className="list-unstyled mb-3">
              {items.map((item, i) => {
                const cleanItem = item.replace(/^[-•]\s*/, '').trim();
                return (
                  <li key={i} className="d-flex align-items-start gap-2 mb-2 text-white-80">
                    <span className="text-primary flex-shrink-0 mt-1">•</span>
                    <span dangerouslySetInnerHTML={{ __html: formatInlineText(cleanItem) }} />
                  </li>
                );
              })}
            </ul>
          );
        }

        // Regular paragraph - process inline formatting
        return (
          <p key={blockIdx} className="mb-3 text-white-80" dangerouslySetInnerHTML={{ __html: formatInlineText(block) }} />
        );
      }).filter(Boolean);
    };

    // Format inline text (bold, italic, etc)
    const formatInlineText = (str) => {
      return str
        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white fw-bold">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em class="text-white-60">$1</em>')
        .replace(/\n/g, '<br />');
    };

    return (
      <div className="result-prose">
        {processContent(mainContent)}
        {tagsContent && (
          <div className="tags-container mt-4 pt-4 border-top border-white-10">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center gap-2">
                <Hash size={16} className="text-primary" />
                <span className="card-label text-primary">Tags</span>
              </div>
              <button
                onClick={() => {
                  const cleanTags = tagsContent
                    .split(/[,\n]+/)
                    .map(tag => tag.trim())
                    .filter(tag => tag.length > 0 && tag !== 'Tags:')
                    .map(tag => tag.replace(/^#+\s*/, ''))
                    .join(', ');
                  copyToClipboard(cleanTags);
                }}
                className="btn btn-sm d-flex align-items-center gap-1 text-primary hover-light"
                style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
              >
                <Copy size={14} /> Copy All
              </button>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {tagsContent
                .split(/[,\n]+/)
                .map(tag => tag.trim())
                .filter(tag => tag.length > 0 && tag !== 'Tags:')
                .map((tag, i) => (
                  <span key={i} className="tag-pill">
                    {tag.replace(/^#+\s*/, '')}
                  </span>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- API Calls ---
  const callGeminiText = async (prompt, key) => {
    const MODEL_ID = localStorage.getItem("geminiTextModel") || "gemini-2.5-flash-lite";
    const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${key}`;
    
    const response = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        generationConfig: { maxOutputTokens: 2048, temperature: 0.7 }
      })
    });

    if (!response.ok) throw new Error("Text API Error");
    const data = await response.json();
    return data?.candidates?.[0]?.content?.parts?.[0]?.text;
  };

  const callGeminiImage = async (prompt, key, aspectRatio = "16:9") => {
    const MODEL_ID = localStorage.getItem("geminiImageModel") || "gemini-2.5-flash-image";
    const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${key}`;
    
    const response = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        generationConfig: {
          responseModalities: ["IMAGE"],
          imageConfig: { aspectRatio }
        }
      })
    });

    if (!response.ok) throw new Error("Image API Error");
    const data = await response.json();
    const imagePart = data?.candidates?.[0]?.content?.parts?.find(p => p.inlineData);
    if (!imagePart) throw new Error("No image returned");
    return `data:${imagePart.inlineData.mimeType};base64,${imagePart.inlineData.data}`;
  };

  const startAutomation = async () => {
    if (!keyword.trim()) return toast.error("Please enter a primary keyword");

    setIsProcessing(true);
    setError("");
    setResults({ research: null, title: "", metadata: null, thumbnail: null, scenes: [], finalResult: null });
    setStepStatus(['idle', 'idle', 'idle', 'idle', 'idle', 'idle']);
    
    try {
      const key = await validateGeminiKey(user.email);
      if (!key) {
        setIsProcessing(false);
        return;
      }

      // STEP 1: Research
      setCurrentStep(0);
      updateStepStatus(0, 'loading');
      const researchPrompt = `Analyze the keyword "${keyword}" for YouTube. 
        Provide the output in clean sections: 
        1. **Top Keywords**: List 10 high-volume keywords.
        2. **Viral Ideas**: List 5 viral video ideas with a brief 1-sentence pitch.
        Use markdown style formatting with **bold** headers.`;
      const researchData = await callGeminiText(researchPrompt, key);
      setResults(prev => ({ ...prev, research: researchData }));
      updateStepStatus(0, 'success');

      // STEP 2: Title
      setCurrentStep(1);
      updateStepStatus(1, 'loading');
      const titlePrompt = `Based on "${keyword}", generate 5 highly clickable YouTube titles. 
        Format them as a numbered list. Use psychological triggers like curiosity or value.`;
      const titleData = await callGeminiText(titlePrompt, key);
      setResults(prev => ({ ...prev, title: titleData }));
      updateStepStatus(1, 'success');

      // STEP 3: Metadata
      setCurrentStep(2);
      updateStepStatus(2, 'loading');
      const metaPrompt = `For a YouTube video about "${keyword}":
        1. Write a compelling SEO description (3 paragraphs).
        2. List 15-20 relevant tags at the end, starting with "Tags:".
        Ensure the description flows naturally.`;
      const metaData = await callGeminiText(metaPrompt, key);
      setResults(prev => ({ ...prev, metadata: metaData }));
      updateStepStatus(2, 'success');

      // STEP 4: Thumbnail
      setCurrentStep(3);
      updateStepStatus(3, 'loading');
      const thumbPrompt = `Create a professional YouTube thumbnail for: "${keyword}".
        Requirements:
        - Bold, attention-grabbing text overlay on the image (use impact font style)
        - High contrast colors (red, yellow, black combination works well)
        - Emotional and intriguing expression/visuals
        - Professional cinematic quality, 8k resolution
        - Include expressive face close-up if relevant
        - Make it click-worthy and viral-worthy
        The text should be short (3-5 words max) and impactful.`;
      const thumbnailImage = await callGeminiImage(thumbPrompt, key);
      setResults(prev => ({ ...prev, thumbnail: thumbnailImage }));
      updateStepStatus(3, 'success');

      // STEP 5: Scenes
      setCurrentStep(4);
      updateStepStatus(4, 'loading');
      const scenePromptsText = await callGeminiText(
        `Generate 8 distinct, descriptive image prompts for video scenes about "${keyword}". 
        Format as a numbered list. Each prompt should be visual and detailed.`, key);
      const individualPrompts = scenePromptsText.split('\n').filter(p => p.trim().length > 10).slice(0, 8);
      
      const generatedScenes = [];
      for (let i = 0; i < individualPrompts.length; i++) {
        try {
          const sceneImg = await callGeminiImage(individualPrompts[i] + ", cinematic style, photorealistic", key);
          generatedScenes.push(sceneImg);
          setResults(prev => ({ ...prev, scenes: [...generatedScenes] }));
        } catch (e) {
          console.error(`Failed to generate scene ${i+1}`, e);
        }
      }
      updateStepStatus(4, 'success');

      // STEP 6: Finalize
      setCurrentStep(5);
      updateStepStatus(5, 'loading');
      const finalPrompt = `Create a content strategy for "${keyword}":
        1. **The Hook**: A script for the first 10 seconds (attention-grabbing).
        2. **Script Structure**: Outline for Intro, Body, and Conclusion.
        3. **SEO Tips**: 3 specific tips to rank this video high.
        Use clear headings.`;
      const finalData = await callGeminiText(finalPrompt, key);
      setResults(prev => ({ ...prev, finalResult: finalData }));
      updateStepStatus(5, 'success');

      toast.success("All tasks completed successfully!");
    } catch (err) {
      console.error(err);
      setError("An error occurred during generation. Please try again.");
      toast.error("Generation failed");
      updateStepStatus(currentStep, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const downloadImage = (url, name) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loadingAuth) return (
    <div className="main-container d-flex align-items-center justify-content-center">
      <Loader2 className="animate-spin text-primary" size={48} />
    </div>
  );

  return (
    <div className="main-container">
      <style>{`
        :root {
          --primary-color: #3b82f6;
          --bg-dark: #0f172a;
          --glass-bg: rgba(255, 255, 255, 0.03);
          --glass-border: rgba(255, 255, 255, 0.08);
        }
        
        body { background-color: var(--bg-dark); } /* Ensure body matches */

        .main-container {
          min-height: 100vh;
          background-color: var(--bg-dark);
          color: #fff;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          position: relative;
          overflow-x: hidden;
        }

        /* --- Blobs --- */
        .bg-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(120px);
          opacity: 0.4;
          pointer-events: none;
          z-index: 0;
        }
        .blob-1 { top: -15%; left: -10%; width: 500px; height: 500px; background: rgba(59, 130, 246, 0.2); }
        .blob-2 { top: 30%; right: -10%; width: 400px; height: 400px; background: rgba(124, 58, 237, 0.15); }
        .blob-3 { bottom: -10%; left: 20%; width: 450px; height: 450px; background: rgba(236, 72, 153, 0.1); }

        /* --- Glass --- */
        .glass-panel {
          background: var(--glass-bg);
          border: 1px solid var(--glass-border);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          box-shadow: 0 10px 40px -10px rgba(0,0,0,0.5);
        }

        /* --- Inputs --- */
        .search-input {
          background: transparent !important;
          border: none !important;
          color: #fff !important;
          font-size: 1.1rem;
          padding: 1.2rem 1.5rem;
          letter-spacing: 0.02em;
        }
        .search-input::placeholder { color: rgba(255,255,255,0.3); font-weight: 300; }
        .search-input:focus { box-shadow: none; outline: none; }

        .btn-generate {
          background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
          color: #0f172a;
          font-weight: 700;
          border-radius: 14px;
          padding: 1rem 2.5rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(255,255,255,0.1);
        }
        .btn-generate:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(255,255,255,0.15); }
        .btn-generate:disabled { opacity: 0.6; transform: none; cursor: not-allowed; }

        /* --- Stepper --- */
        .step-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 16px;
          padding: 1.25rem;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .step-card.active {
          background: rgba(255,255,255,0.05);
          border-color: rgba(255,255,255,0.1);
        }
        .step-card .icon-box {
          width: 44px; height: 44px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.03);
          color: rgba(255,255,255,0.4);
          flex-shrink: 0;
        }
        .step-card .icon-box.success { background: rgba(16, 185, 129, 0.1); color: #10b981; }
        .step-card .icon-box.loading { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
        
        .indicator {
          position: absolute; right: 0; top: 0; bottom: 0;
          width: 3px; background: var(--primary-color);
        }

        /* --- Results Display --- */
        .result-prose {
          font-size: 0.95rem;
          line-height: 1.8;
          color: rgba(255, 255, 255, 0.9);
        }
        .result-prose strong { color: #fff; font-weight: 700; }
        .result-prose ul { padding-left: 0; list-style: none; }
        .result-prose h4 { font-size: 1rem; color: #fff; border-left: 3px solid var(--primary-color); padding-left: 12px; }
        .result-prose p { line-height: 1.8; }
        
        .card-label {
          font-size: 0.7rem;
          letter-spacing: 0.1em;
          font-weight: 700;
          text-transform: uppercase;
        }

        .result-box {
          background: rgba(255,255,255,0.02);
          border-radius: 16px;
          padding: 1.5rem;
          border: 1px solid rgba(255,255,255,0.05);
          transition: all 0.3s;
        }
        .result-box:hover { border-color: rgba(255,255,255,0.1); }

        .copy-btn {
          position: absolute; top: 1rem; right: 1rem;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          padding: 0.5rem;
          border-radius: 10px;
          color: rgba(255,255,255,0.5);
          opacity: 0;
          transition: all 0.2s;
          cursor: pointer;
          z-index: 10;
        }
        .result-box:hover .copy-btn { opacity: 1; }
        .copy-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
        .hover-light:hover { background: rgba(59, 130, 246, 0.25) !important; }

        .tag-pill {
          background: rgba(59, 130, 246, 0.15);
          border: 1px solid rgba(59, 130, 246, 0.3);
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.85);
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .tag-pill:hover { background: rgba(59, 130, 246, 0.3); border-color: rgba(59, 130, 246, 0.5); color: #fff; transform: translateY(-1px); }

        .scene-card {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          aspect-ratio: 16/9;
          border: 1px solid rgba(255,255,255,0.1);
          transition: transform 0.3s;
        }
        .scene-card:hover { transform: scale(1.02); }
        .scene-card img { width: 100%; height: 100%; object-fit: cover; }
        
        .scene-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 50%);
          display: flex; align-items: flex-end; justify-content: center;
          padding-bottom: 1rem;
          opacity: 0; transition: opacity 0.3s;
        }
        .scene-card:hover .scene-overlay { opacity: 1; }

        /* Utilities */
        .text-white-40 { color: rgba(255,255,255,0.4); }
        .text-white-50 { color: rgba(255,255,255,0.5); }
        .bg-white-10 { background: rgba(255,255,255,0.1); }
        .border-white-10 { border-color: rgba(255,255,255,0.1) !important; }
        
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }

        /* --- Image Lightbox --- */
        .image-lightbox {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: rgba(0, 0, 0, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }
        .lightbox-content {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
        }
        .lightbox-close {
          position: absolute;
          top: -3rem;
          right: 0;
          background: rgba(255,255,255,0.1);
          border: none;
          color: #fff;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        .lightbox-close:hover { background: rgba(255,255,255,0.2); }
        .lightbox-image {
          max-width: 100%;
          max-height: 80vh;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.5);
        }
        .lightbox-actions {
          display: flex;
          justify-content: center;
          margin-top: 1.5rem;
        }
        .lightbox-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255,255,255,0.1);
          border: none;
          color: #fff;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          z-index: 10;
        }
        .lightbox-nav:hover { background: rgba(255,255,255,0.25); }
        .lightbox-prev { left: -5rem; }
        .lightbox-next { right: -5rem; }
        .lightbox-counter {
          position: absolute;
          top: -3rem;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(255,255,255,0.15);
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 0.85rem;
          color: rgba(255,255,255,0.9);
        }
        .lightbox-image-container { position: relative; }

        /* --- Thumbnail Card --- */
        .thumbnail-card {
          border-radius: 20px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.1);
          transition: all 0.3s ease;
        }
        .thumbnail-card:hover {
          border-color: rgba(59, 130, 246, 0.4);
          transform: translateY(-2px);
          box-shadow: 0 15px 40px rgba(59, 130, 246, 0.15);
        }
        .thumbnail-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 1.5rem;
          background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%);
          display: flex;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s;
        }
        .thumbnail-card:hover .thumbnail-overlay { opacity: 1; }
        .thumbnail-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: linear-gradient(135deg, #facc15 0%, #f59e0b 100%);
          color: #000;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 4px;
        }
      `}</style>

      
      
      {/* Background Blobs */}
      <div className="bg-blob blob-1"></div>
      <div className="bg-blob blob-2"></div>
      <div className="bg-blob blob-3"></div>

      <div className="container py-5 position-relative z-1">
        {/* Header */}
        <header className="text-center mb-6">
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="display-3 fw-bolder mb-3"
            style={{ letterSpacing: '-0.02em', lineHeight: '1.1' }}
          >
            All In <span style={{ color: 'var(--primary-color)' }}>YouTube</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="lead text-white-40 col-lg-6 mx-auto"
          >
            Enter a keyword to generate optimized titles, descriptions, thumbnails, and scene visuals in one click.
          </motion.p>
        </header>

        {/* Search Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="col-xl-8 mx-auto mb-5"
        >
          <div className="glass-panel p-2 d-flex flex-column flex-md-row gap-2 align-items-stretch align-items-md-center">
            <input 
              type="text" 
              placeholder="Enter keyword (e.g. 'Mindfulness Meditation')" 
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              disabled={isProcessing}
              className="form-control search-input flex-grow-1"
            />
            <button 
              onClick={startAutomation}
              disabled={isProcessing}
              className="btn btn-generate d-flex align-items-center justify-content-center gap-2"
            >
              {isProcessing ? <Loader2 className="animate-spin" size={18} /> : <Rocket size={18} />}
              <span>{isProcessing ? "Processing..." : "Generate Assets"}</span>
            </button>
          </div>
          {error && (
            <div className="alert alert-danger mt-3 d-flex align-items-center justify-content-center gap-2 py-2 rounded-pill bg-transparent border-danger text-danger small">
              <AlertCircle size={16} /> {error}
            </div>
          )}
        </motion.div>

        {/* Main Grid */}
        <div className="row g-4">
          {/* Stepper Sidebar */}
          <div className="col-lg-4">
            <div className="d-flex flex-column gap-3">
              {steps.map((step, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * index }}
                  className={`step-card d-flex align-items-center gap-3 ${currentStep === index ? 'active' : ''}`}
                >
                  <div className={`icon-box 
                    ${stepStatus[index] === 'success' ? 'success' : 
                      stepStatus[index] === 'loading' ? 'loading' : ''}`}
                  >
                    {stepStatus[index] === 'success' ? <CheckCircle2 size={20} /> : 
                     stepStatus[index] === 'loading' ? <Loader2 className="animate-spin" size={20} /> : step.icon}
                  </div>
                  
                  <div className="flex-grow-1">
                    <h4 className="mb-0 fw-semibold text-white" style={{fontSize: '0.95rem'}}>
                      {step.name}
                    </h4>
                    <small className="text-white-40" style={{fontSize: '0.75rem'}}>
                      {stepStatus[index] === 'loading' ? 'Processing...' : step.description}
                    </small>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Results Area */}
          <div className="col-lg-8">
            <AnimatePresence mode="wait">
              {results.research || results.title || results.metadata || results.thumbnail || results.scenes.length > 0 ? (
                <motion.div 
                  key="results"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="glass-panel p-4 p-md-5"
                >
                  <div className="d-flex justify-content-between align-items-center mb-4 pb-4 border-bottom border-white-10">
                    <div className="d-flex align-items-center gap-2">
                      <div className="bg-primary/10 rounded-circle p-2 d-flex align-items-center justify-content-center">
                        <LayoutDashboard size={18} className="text-primary" />
                      </div>
                      <h2 className="h5 fw-bold mb-0">Output Console</h2>
                    </div>
                  </div>

                  {/* Section 1: Research */}
                  {results.research && (
                    <section className="mb-5">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <Search size={14} className="text-primary" />
                        <span className="card-label text-primary">Research & Trends</span>
                      </div>
                      <div className="result-box position-relative">
                        {renderFormattedText(results.research)}
                        <button onClick={() => copyToClipboard(results.research)} className="copy-btn"><Copy size={16} /></button>
                      </div>
                    </section>
                  )}

                  {/* Section 2: Titles */}
                  {results.title && (
                    <section className="mb-5">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <ListOrdered size={14} style={{color: '#a855f7'}} />
                        <span className="card-label" style={{color: '#a855f7'}}>Seo Titles</span>
                      </div>
                      <div className="result-box position-relative">
                        {renderFormattedText(results.title)}
                        <button onClick={() => copyToClipboard(results.title)} className="copy-btn"><Copy size={16} /></button>
                      </div>
                    </section>
                  )}

                  {/* Section 3: Metadata */}
                  {results.metadata && (
                    <section className="mb-5">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <FileText size={14} style={{color: '#ec4899'}} />
                        <span className="card-label" style={{color: '#ec4899'}}>Description & Tags</span>
                      </div>
                      <div className="result-box position-relative">
                        {renderFormattedText(results.metadata)}
                        <button onClick={() => copyToClipboard(results.metadata)} className="copy-btn"><Copy size={16} /></button>
                      </div>
                    </section>
                  )}

                  {/* Section 4: Thumbnail */}
                  {results.thumbnail && (
                    <section className="mb-5">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <ImageIcon size={14} style={{color: '#facc15'}} />
                        <span className="card-label" style={{color: '#facc15'}}>Thumbnail Concept</span>
                      </div>
                      <div className="result-box p-0 overflow-hidden position-relative thumbnail-card" onClick={() => { setSelectedImage(results.thumbnail); setCurrentSlideIndex(0); }}>
                        <div className="ratio ratio-16x9">
                          <img src={results.thumbnail} alt="Thumbnail" className="img-fluid" />
                        </div>
                        <div className="thumbnail-overlay">
                          <button onClick={(e) => { e.stopPropagation(); downloadImage(results.thumbnail, "thumbnail"); }} className="btn btn-light btn-sm rounded-pill px-3 d-flex align-items-center gap-2">
                            <Download size={14} /> Download HD
                          </button>
                        </div>
                        <div className="thumbnail-badge">
                          <Sparkles size={12} /> YouTube Thumbnail
                        </div>
                      </div>
                    </section>
                  )}

                  {/* Section 5: Scenes */}
                  {results.scenes.length > 0 && (
                    <section className="mb-5">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <Clapperboard size={14} style={{color: '#3b82f6'}} />
                        <span className="card-label" style={{color: '#3b82f6'}}>Scene Visuals ({results.scenes.length}/8)</span>
                      </div>
                      <div className="row g-3">
                        {results.scenes.map((scene, i) => (
                          <div key={i} className="col-6 col-md-3">
                            <motion.div
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="scene-card shadow-sm"
                              style={{ cursor: 'pointer' }}
                              onClick={() => { setSelectedImage(scene); setCurrentSlideIndex(i + 1); }}
                            >
                              <img src={scene} alt={`Scene ${i+1}`} />
                              <div className="scene-overlay" style={{ opacity: 1 }}>
                                <button onClick={(e) => { e.stopPropagation(); downloadImage(scene, `scene-${i+1}`); }} className="btn btn-light btn-sm rounded-circle p-2">
                                  <Download size={16} />
                                </button>
                              </div>
                              <span className="position-absolute top-0 start-0 m-2 badge rounded-pill bg-dark/50 text-white small">
                                0{i+1}
                              </span>
                            </motion.div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Section 6: Final Strategy */}
                  {results.finalResult && (
                    <section>
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <PenTool size={14} style={{color: '#f97316'}} />
                        <span className="card-label" style={{color: '#f97316'}}>Script Hook & Strategy</span>
                      </div>
                      <div className="result-box position-relative">
                        {renderFormattedText(results.finalResult)}
                        <button onClick={() => copyToClipboard(results.finalResult)} className="copy-btn"><Copy size={16} /></button>
                      </div>
                    </section>
                  )}

                </motion.div>
              ) : (
                <motion.div 
                  key="empty"
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  className="h-100 d-flex flex-column align-items-center justify-content-center text-center py-6"
                  style={{ minHeight: '500px' }}
                >
                  <div className="mb-4">
                    <Youtube size={48} strokeWidth={1} className="text-white-40" />
                  </div>
                  <h4 className="text-white-50 mb-2">Ready to Create</h4>
                  <p className="text-white-40 col-md-8 mx-auto">
                    Your AI-generated assets will appear here.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Image Lightbox Modal with Slider */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="image-lightbox"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="lightbox-close"
                onClick={() => setSelectedImage(null)}
              >
                <X size={24} />
              </button>

              {/* Navigation Arrows */}
              {results.scenes.length > 0 && (
                <>
                  <button
                    className="lightbox-nav lightbox-prev"
                    onClick={() => {
                      const allImages = [results.thumbnail, ...results.scenes];
                      const newIndex = currentSlideIndex > 0 ? currentSlideIndex - 1 : allImages.length - 1;
                      setCurrentSlideIndex(newIndex);
                      setSelectedImage(allImages[newIndex]);
                    }}
                  >
                    <ChevronLeft size={32} />
                  </button>
                  <button
                    className="lightbox-nav lightbox-next"
                    onClick={() => {
                      const allImages = [results.thumbnail, ...results.scenes];
                      const newIndex = currentSlideIndex < allImages.length - 1 ? currentSlideIndex + 1 : 0;
                      setCurrentSlideIndex(newIndex);
                      setSelectedImage(allImages[newIndex]);
                    }}
                  >
                    <ChevronRight size={32} />
                  </button>
                </>
              )}

              <img src={selectedImage} alt="Preview" className="lightbox-image" />

              {/* Image Counter */}
              {results.scenes.length > 0 && (
                <div className="lightbox-counter">
                  {currentSlideIndex + 1} / {results.scenes.length + 1}
                </div>
              )}

              <div className="lightbox-actions">
                <button
                  onClick={() => downloadImage(selectedImage, `image-${Date.now()}`)}
                  className="btn btn-light rounded-pill px-4 d-flex align-items-center gap-2"
                >
                  <Download size={18} /> Download HD
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AllInYouTube;